// IXIARO® is the only product in this PGD. There are two schedules —
// standard (days 0 and 28) and rapid (days 0 and 7). The rapid schedule is
// licensed for adults 18–65 only. Its use in children aged 2 months to 17
// years and adults aged 65 and over is off-label. Children aged 2–35
// months receive 0.25 mL; all other ages receive 0.5 mL. Both doses are
// modelled as brands to allow the runner to auto-select the correct volume
// by age — volumes are embedded in the brand doseDescription.

import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

registerTier2Config({
  pgdId: PGD_IDS.JAPANESE_ENC,
  displayName: "Japanese encephalitis (IXIARO\u00ae)",
  serviceType: "administration",
  minimumAgeYears: 0,
  indicationSummary:
    "Travelling to or residing in a Japanese encephalitis risk country where NaTHNaC assessment indicates vaccination is recommended, or at occupational risk (e.g. laboratory staff handling JE virus). Protection is not established until after the second dose.",

  brands: [
    {
      brandId: "ixiaro-infant",
      brandName: "IXIARO\u00ae (2\u201335 months \u2014 0.25 mL dose)",
      minimumAgeYears: 0,
      maximumAgeYears: 2,
      route: "IM",
      doseDescription:
        "0.25 mL per dose (half the syringe contents, prepared per SmPC)",
      administrationNotes:
        "The 0.25 mL paediatric dose is prepared from the standard 0.5 mL pre-filled syringe per the manufacturer's SmPC instructions. Expel half the syringe contents before administration. Administer by intramuscular injection into the anterolateral thigh in infants and young children. The solution should be clear and colourless to slightly yellowish. Inspect before use.",
    },
    {
      brandId: "ixiaro-standard",
      brandName: "IXIARO\u00ae (3 years and over \u2014 0.5 mL dose)",
      minimumAgeYears: 3,
      route: "IM",
      doseDescription: "0.5 mL per dose",
      administrationNotes:
        "Administer by intramuscular injection into the deltoid muscle. The solution should be clear and colourless to slightly yellowish. Inspect before use. Do not use the gluteal region.",
    },
  ],

  schedules: [
    {
      scheduleId: "je-standard",
      label: "Standard schedule (days 0 and 28)",
      offLabel: false,
      doses: [
        {
          doseNumber: 1,
          label: "Day 0 \u2014 first dose",
        },
        {
          doseNumber: 2,
          label: "Day 28 \u2014 second dose",
          intervalFromDose1Days: 28,
        },
      ],
      boosterInfo:
        "A first booster dose is recommended 1\u20132 years after completing the primary course for patients at continued risk. For the second booster and beyond, refer to the Green Book Chapter 20 for children and adults aged 65 and over \u2014 a 10-year booster interval applies for adults aged 18\u201365 only.",
      completionNotes:
        "Complete the course at least 1 week before potential exposure. Protection is not established until after the second dose \u2014 advise the patient to continue mosquito-bite avoidance until full protection is achieved.",
    },
    {
      scheduleId: "je-rapid",
      label:
        "Rapid schedule (days 0 and 7) \u2014 adults 18\u201365 only within licence; off-label for ages under 18 and 65 and over",
      offLabel: true,
      offLabelRationale:
        "The rapid IXIARO\u00ae schedule (days 0 and 7) is licensed for adults aged 18\u201365 where there is insufficient time for the standard schedule. Its use in children aged 2 months to 17 years and in adults aged 65 and over is outside the product licence, supported by Green Book guidance where travel is imminent and the standard schedule cannot be completed. Use requires documented rationale and patient consent to off-label use.",
      doses: [
        {
          doseNumber: 1,
          label: "Day 0 \u2014 first dose",
        },
        {
          doseNumber: 2,
          label: "Day 7 \u2014 second dose",
          intervalFromDose1Days: 7,
        },
      ],
      boosterInfo:
        "A first booster dose is recommended 1\u20132 years after completing the primary course for patients at continued risk.",
      completionNotes:
        "Complete the course at least 1 week before potential exposure. Protection is not established until after the second dose. Off-label for ages under 18 and 65 and over \u2014 patient consent must be documented.",
    },
  ],

  capturesPriorDoseHistory: true,

  exclusions: [
    {
      checkId: "je-ex-age",
      question: "Is the patient under 2 months of age?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "IXIARO\u00ae is not licensed for patients under 2 months of age. Do not proceed.",
      referralMessage: "Refer to the patient's GP or a paediatric specialist.",
    },
    {
      checkId: "je-ex-hypersensitivity",
      question:
        "Does the patient have known hypersensitivity to any component or production residual of IXIARO\u00ae, including protamine sulphate, formaldehyde, bovine serum albumin, host-cell DNA, sodium metabisulphite, or host-cell protein?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "IXIARO\u00ae is contraindicated in this patient. Do not proceed.",
      referralMessage: "Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "je-ex-anaphylaxis-dose1",
      question:
        "Did the patient have a hypersensitivity reaction following their first dose of IXIARO\u00ae at this or a previous visit?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "A hypersensitivity reaction following the first dose of IXIARO\u00ae is a contraindication to the second dose. Do not proceed.",
      referralMessage:
        "Refer to the patient's GP or an allergy specialist for individual risk assessment.",
    },
    {
      checkId: "je-ex-pregnancy",
      question: "Is the patient known or suspected to be pregnant?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "IXIARO\u00ae should not be used in pregnancy except where an individual risk-benefit assessment has been completed by a specialist. Do not proceed under this PGD.",
      referralMessage:
        "Refer to the patient's GP or obstetrician for individual risk-benefit assessment before vaccination.",
    },
  ],

  cautions: [
    {
      checkId: "je-caut-bleeding",
      question:
        "Does the patient have a bleeding disorder or is the patient on anticoagulant therapy?",
      cautionMessage:
        "Administer by intramuscular injection with care using a fine-gauge needle with firm pressure applied for at least two minutes afterwards. For patients on stable warfarin with INR within therapeutic range, IM injection may proceed. The subcutaneous route is possible but is associated with more local reactions. Document the clinical decision.",
    },
    {
      checkId: "je-caut-immunosuppressed",
      question: "Is the patient immunosuppressed due to disease or treatment?",
      cautionMessage:
        "Immunosuppression may reduce the immune response to IXIARO\u00ae. Vaccination should still proceed as the vaccine is inactivated and safe. Counsel that protection may be suboptimal.",
    },
    {
      checkId: "je-caut-breastfeeding",
      question: "Is the patient breastfeeding?",
      cautionMessage:
        "IXIARO\u00ae is an inactivated vaccine. Breastfeeding is not a contraindication. Vaccination may proceed.",
    },
    {
      checkId: "je-caut-syncope",
      question:
        "Is there any history of syncope (fainting) following a previous injection or vaccination?",
      cautionMessage:
        "Syncope can occur following vaccination. Ensure the patient is seated or lying down during and for the duration of the observation period after administration.",
    },
  ],

  supplyGates: [],

  requiresICVP: false,
  requiresLariamCard: false,
  requiresYellowCardDiscussion: false,
  requiresCHISNotificationUnder19: true,
  requiresGPNotification: true,
  standardObservationMinutes: 15,
  extendedObservationMinutes: 30,

  patientAdvicePoints: [
    "This vaccine protects against Japanese encephalitis only. It does not protect against tick-borne encephalitis or other mosquito-borne or tick-borne diseases.",
    "Protection is not established until after the second dose. Continue to use mosquito-bite avoidance measures (repellent containing DEET, covered clothing, permethrin-treated clothing and nets) until the full course is complete and for the duration of your trip.",
    "A booster dose is recommended 1\u20132 years after completing the primary course if you remain at risk.",
    "If you develop fever, headache, neck stiffness, confusion, or any other neurological symptoms during or after travel, seek urgent medical attention and tell the clinician you may have been exposed to Japanese encephalitis.",
    "If you had an allergic reaction after your first dose, do not take the second dose \u2014 seek medical advice.",
  ],
});
